import type { ChatMessage, ChatMessageBody } from '@wundo/shared/chat.ts';
import type { RoleplayDebriefRemark } from '@wundo/shared/roleplay.ts';

/** One bubble of the transcript, and the remarks that go under it. */
export interface TranscriptTurn {
  /** The first message's id, which names the bubble. */
  id: string;
  author: ChatMessage['author'];
  text: string;
  remarks: RoleplayDebriefRemark[];
}

/* The words of a message, read the way the debrief reads them. */
function bodyText(body: ChatMessageBody): string {
  if (body.type === 'card') {
    return [body.header, body.body].filter(Boolean).join('\n');
  }
  return body.text ?? '';
}

/**
 * The conversation as bubbles. A spoken reply can be stored as several
 * messages in a row; they are one turn, so they are read as one bubble, with
 * the remarks on any of them under it.
 */
export function transcriptTurns(
  messages: readonly ChatMessage[],
  remarks: readonly RoleplayDebriefRemark[],
): TranscriptTurn[] {
  const turns: TranscriptTurn[] = [];

  for (const message of messages) {
    const text = bodyText(message.body);
    if (text === '') {
      continue;
    }

    const onThis = remarks.filter((remark) => remark.message_id === message.id);
    const last = turns.at(-1);

    if (last?.author === message.author) {
      last.text = `${last.text} ${text}`;
      last.remarks.push(...onThis);
    } else {
      turns.push({ id: message.id, author: message.author, text, remarks: [...onThis] });
    }
  }

  return turns;
}
